/// <amd-module name="Build/Extensions/Bitbucket/BitbucketWizard" />
import * as React from "react";

import * as Build from "Build/Client/Contracts/Build";

import { WizardStateKeys } from "Build/Designer/Constants";
import * as VisibilityMismatch from "Build/Designer/VisibilityMismatchWarning";
import { clearWarning, showWarning } from "Build/Designer/WarningBanner";
import { StepCompletedCallback, WizardState } from "Build/Designer/Wizard.types";

import { ConnectionSelection } from "Build/Extensions/Bitbucket/ConnectionSelection";
import { RepositorySelection } from "Build/Extensions/Bitbucket/RepositorySelection";
import * as BitbucketResources from "Build/Extensions/Bitbucket/Resources";
import { Constant, UserAuthorization, IServiceConnectionCreationService } from "Build/Extensions/Connection-Services/ServiceConnectionCreationService";
import { ServiceConnectionData } from "Build/Extensions/Connection-Services/ServiceConnectionRetrieverService";

import { ITfsPageService } from "Tfs/Platform/Page";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssHistoryService, StateMergeOptions } from "VSS/Platform/History";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";

export enum Steps {
    Loading = 1,
    SelectConnection = 2,
    SelectRepository = 3
}

interface Props {
    wizardState: WizardState;
    onComplete: StepCompletedCallback;
    onUpdate: (action: string, properties?: { [key: string]: any }) => void;
    onTTI: () => void;
}

interface State {
    stepNumber: number;
    connectionId?: string;
    repositoryId?: string;
    errorMessage?: string;
}

export class BitbucketReposWizard extends VssComponent<Props, State, IVssComponentContext> {
    private _projectId: string;

    constructor(props: Props, context: IVssComponentContext) {
        super(props, context);

        const pageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const pageData = pageService.getData();
        this._projectId = pageData!.project!.id;

        this.state = this._getState(props);
    }

    public render(): JSX.Element {
        return (<>
        {
            this.state.errorMessage &&
            <MessageCard className={"bitbucket-wizard-error"} severity={MessageCardSeverity.Error}>
                {this.state.errorMessage}
            </MessageCard>
        }
        {
            this._renderStep()
        }
        </>);
    }

    public componentWillReceiveProps(newProps: Props) {
        this.setState(this._getState(newProps));
    }

    public componentDidUpdate(prevProps: Props, prevState: State) {
        // When moving back from selecting a repository to selecting a connection, remove any error messages
        if (prevState && prevState.stepNumber == Steps.SelectRepository && this.state.stepNumber == Steps.SelectConnection) {
            this._clearErrorMessage();
        }
    }

    public componentDidMount() {
        // clear when the user presses 'back' and there is a banner displayed
        clearWarning(this.context.pageContext, VisibilityMismatch.bannerId);

        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const dataProviderId = "ms.vss-build-web.bitbucket-serviceconnections-data-provider";
        this.trackPromise(
            contributionService.getDataAsync<ServiceConnectionData>(dataProviderId, { "endpointType": this.props.wizardState.sourceProvider }, true)
        ).promise.then((serviceConnectionData) => {
            const completedOAuth = !!this.props.wizardState.key;
            if (completedOAuth) {
                // Once a user has completed OAuth (after source provider selection or authorize in connection selection)
                // create/update service connection as appropriate
                const serviceConnections = (serviceConnectionData && serviceConnectionData.serviceConnections) || [];
                const userAuthorization = {
                    scheme: Constant.OAuth2Scheme,
                    strongboxKey: this.props.wizardState.key,
                    displayName: this.props.wizardState.displayName,
                    avatarUrl: this.props.wizardState.avatarurl
                } as UserAuthorization;

                const service = this.context.pageContext.getService<IServiceConnectionCreationService>("IServiceConnectionCreationService");

                return service.createOrUpdateEndpointForUserAuth(this.context, this.props.wizardState.sourceProvider, this._projectId, serviceConnections, userAuthorization,
                    x => x.displayName === this.props.wizardState.displayName && x.scheme === Constant.OAuth2Scheme)
                    .then((serviceEndpoint) => this._onSelectConnection(serviceEndpoint.id));
            } else if (!this.props.wizardState.connectionId && !this.props.wizardState.showSelectConnection) {
                // Automatically OAuth
                const service = this.context.pageContext.getService<IServiceConnectionCreationService>("IServiceConnectionCreationService");
                service.createAuthRequest(this.context, this.props.wizardState.sourceProvider, this._projectId, false, this._onAuthError);
            } else {
                // The user has manually selected a connection
                const exception = contributionService.getDataProviderExceptions()[dataProviderId];
                if (!!exception) {
                    this.setState({ errorMessage: exception && exception.message });
                }
            }
        }).catch((error: Error) => {
            this.setState({ errorMessage: error && error.message });
        });
    }

    private _renderStep(): JSX.Element {
        switch (this.state.stepNumber) {
            case Steps.Loading:
                return <Spinner size={SpinnerSize.large}/>;
            default:
            case Steps.SelectConnection:
                return <ConnectionSelection onSelectConnection={this._onSelectConnection}
                                            serviceName={BitbucketResources.Bitbucket}
                                            sourceProvider={this.props.wizardState.sourceProvider}
                                            onErrorMessage={this._onErrorMessage}/>;
            case Steps.SelectRepository:
                return <RepositorySelection onSelectRepository={this._onSelectRepository}
                                            connectionId={this.state.connectionId!}
                                            serviceName={BitbucketResources.Bitbucket}
                                            sourceProvider={this.props.wizardState.sourceProvider}
                                            strongBoxKey={this.props.wizardState.key}
                                            onRepositoryListUpdate={this._onRepositoryListUpdate}
                                            onErrorMessage={this._onErrorMessage}
                                            onTTI={this.props.onTTI}/>;
        }
    }

    private _onSelectConnection = (connectionId: string): void => {
        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");

        const currentState = historyService.getState();

        // remove everything related with redirecting the page
        const redirectedBack = !!(currentState['key']);
        if (redirectedBack) {
            ["avatarurl", "repositorydisplayNameId", "key", "showSelectConnection", "displayName"]
                .filter(x => currentState[x])
                .forEach(x => delete currentState[x]);
        }

        const url: string = historyService.generateUrl({
            ...currentState,
            connectionId: connectionId
        }, StateMergeOptions.none);

        historyService.pushState(undefined, url);

        this.setState({
            stepNumber: Steps.SelectRepository,
            connectionId: connectionId
        });
    }

    private _onSelectRepository = (repository: Build.SourceRepository, properties: { [key: string]: string }): void => {
        if (VisibilityMismatch.isVisibilityMismatch(this.context.pageContext, repository)) {
            var warningBanner = VisibilityMismatch.getBannerContent(this.context.pageContext, repository);
            showWarning(this.context.pageContext, warningBanner);
        }

        this.props.onComplete({
            repositoryId: repository.id,
            repositoryName: repository.id,
            branch: repository.defaultBranch,
            connectionId: this.state.connectionId!,
            repositoryUrl: properties.repositoryUrl
        }, properties);
    }

    private _getState(props: Props): State {
        const connectionId: string = props.wizardState[WizardStateKeys.ConnectionId];
        const repositoryId: string = props.wizardState[WizardStateKeys.RepositoryId];

        const stepNumber = (() => {
            if (connectionId && !repositoryId) {
                return Steps.SelectRepository;
            } else if (connectionId && repositoryId) {
                // The URL has the repo id of the repo to use.
                return Steps.SelectRepository
            } else if (!props.wizardState.showSelectConnection) {
                // We are in the process of OAuthing with Bitbucket
                return Steps.Loading;
            } else {
                return Steps.SelectConnection;
            }
        })();

        return {
            stepNumber,
            connectionId,
            repositoryId
        };
    }

    private _onErrorMessage = (error: string): void => {
        this.setState({ errorMessage: error });
    }

    private _clearErrorMessage = () => {
        this.setState({
            errorMessage: undefined
        });
    }

    private _onRepositoryListUpdate = (action: string, properties?: { [key: string]: any }): void => {
        this.props.onUpdate(action, properties);
    }

    private _onAuthError = (e: any): void => {
        // on error (which should happen only when in the 'LoadingOAuth' step) show the connection page
        // this should allow the user to see any authorization errors
        this.setState({
            stepNumber: Steps.SelectConnection,
            errorMessage: e
        });
    }
}

Components.add("bitbucket-wizard", BitbucketReposWizard);
